'use client';

/**
 * Top-level authentication provider responsible for:
 *  - reading env-driven config (GROUPS/features, endpoints) so we know *why* a user can access things
 *  - keeping JWTs in sync with localStorage and refreshing them before expiry
 *  - exposing derived `user`, `isReady`, and mutation helpers to the rest of the app
 * The heavy lifting (config fetch, token math, user construction) lives in the /provider/auth helpers,
 * so this file only orchestrates when each subsystem runs.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { User } from '@/services/auth-service';
import type { LoginResponse } from '@/types/api-types';
import { appConfig, getApiEndpoint } from '@/config/app-config';
import { buildUserFromToken } from '@/contexts/provider/auth/user-factory';
import { parseJsonResponse } from '@/utils/http';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isReady: boolean;
  photoUrl: string | null;
  displayName: string | null;
  updatePhotoUrl: (url: string | null) => void;
  login: (_email: string, _password: string) => Promise<LoginResponse>;
  logout: () => void;
  setNewPassword: (_email: string, _oldPassword: string, _newPassword: string) => Promise<LoginResponse>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);

  const groupsCfgRef = useRef(appConfig.GROUPS);
  const refreshTimer = useRef<number | null>(null);
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  const clearRefreshTimer = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (refreshTimer.current != null) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
  }, []);

  // Removed localStorage-dependent token management functions
  // Authentication is now handled via secure HttpOnly cookies

  const performLogout = useCallback(async (options?: { redirect?: boolean }) => {
    console.warn('[Auth] Logout called');
    clearRefreshTimer();

    try {
      // Call backend logout endpoint to clear cookies
      const apiEndpoint = getApiEndpoint();
      await fetch(`${apiEndpoint}/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Include cookies for logout
      });
    } catch (error) {
      console.warn('[Auth] Logout API call failed:', error);
      // Continue with local cleanup even if API call fails
    }

    // Clear local state
    setUser(null);
    setPhotoUrl(null);
    setDisplayName(null);

    if (typeof window !== 'undefined') {
      console.warn('[Auth] Clearing storage and redirecting');
      window.sessionStorage.clear();

      // Keep cache cleanup for other non-token data
      window.localStorage.removeItem('numa_kb_cache');

      const currentPath = window.location.pathname || '/';
      const defaultRedirect = currentPath !== '/login' && currentPath !== '/';
      const shouldRedirect = options?.redirect ?? defaultRedirect;
      if (shouldRedirect) {
        console.warn('[Auth] Redirecting to /login');
        window.location.href = '/login';
      }
    }
  }, [clearRefreshTimer]);

  const logout = useCallback(() => {
    performLogout({ redirect: true });
  }, [performLogout]);

  const login = useCallback(
      async (email: string, password: string): Promise<LoginResponse> => {
        setIsLoading(true);
        try {
          const apiEndpoint = getApiEndpoint();
          const res = await fetch(`${apiEndpoint}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // Include cookies in request
            body: JSON.stringify({ email: email.toLowerCase(), password }),
          });

          const out = await parseJsonResponse<{ error?: string; requiresNewPassword?: boolean }>(res);

          if (!res.ok) {
            const fallbackError = res.status >= 500
              ? 'Authentication service is temporarily unavailable. Please try again later.'
              : `Login failed (${res.status})`;
            return {
              success: false,
              error: out?.error || fallbackError,
            };
          }

          if (out?.requiresNewPassword) return { success: false, requiresNewPassword: true };

          // Login successful - cookies are set automatically by browser
          // Check authentication status via /auth/me endpoint
          await checkAuthStatus();
          return { success: true };

        } catch (e: unknown) {
          console.error('Login error:', e);
          return { success: false, error: e instanceof Error ? e.message : 'Login failed' };
        } finally {
          setIsLoading(false);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
  );

  const setNewPassword = useCallback(
      async (email: string, oldPassword: string, newPassword: string): Promise<LoginResponse> => {
        setIsLoading(true);
        try {
          const apiEndpoint = getApiEndpoint();
          const res = await fetch(`${apiEndpoint}/auth/set-new-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: email.toLowerCase(), oldPassword, newPassword }),
          });

          const out = await parseJsonResponse<{ success?: boolean; error?: string }>(res);

          if (res.ok && out?.success) {
            return await login(email, newPassword);
          }

          const fallbackError = res.status >= 500
            ? 'Password update service is temporarily unavailable. Please try again later.'
            : `Failed to set new password (${res.status})`;
          return { success: false, error: out?.error || fallbackError };
        } catch (e: unknown) {
          console.error('Error setting new password:', e);
          return { success: false, error: e instanceof Error ? e.message : 'Failed to set new password' };
        } finally {
          setIsLoading(false);
        }
      },
      [login],
  );

  const checkAuthStatus = useCallback(async () => {
    const apiEndpoint = getApiEndpoint();
    const wasAuthenticated = !!userRef.current;

    // Helper: call /auth/me and return result if authenticated, null otherwise
    const fetchMe = async () => {
      const res = await fetch(`${apiEndpoint}/auth/me`, { method: 'GET', credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      return data.authenticated && data.user ? data : null;
    };

    // Helper: apply authenticated user from /auth/me result
    const applyUser = (result: { user: { userId: string; email: string; name: string; groups?: string[]; photoUrl?: string; displayName?: string } }) => {
      const decodedUser = buildUserFromToken(
        {
          sub: result.user.userId,
          email: result.user.email,
          name: result.user.name,
          'https://aws.amazon.com/tags': { principal_tags: { Groups: result.user.groups ?? [] } },
        },
        groupsCfgRef.current,
      );
      setUser(decodedUser);
      setPhotoUrl(result.user.photoUrl || null);
      setDisplayName(result.user.displayName || null);
    };

    // First attempt
    try {
      const result = await fetchMe();
      if (result) { applyUser(result); return; }
    } catch (error) {
      console.warn('[Auth] Auth check failed:', error);
    }

    // First attempt failed — try token refresh if user was previously signed in
    if (wasAuthenticated) {
      try {
        const { authService } = await import('@/services/auth-service');
        const refreshed = await authService.refreshSession();
        if (refreshed) {
          const retryResult = await fetchMe();
          if (retryResult) {
            console.warn('[Auth] Session recovered via token refresh');
            applyUser(retryResult);
            return;
          }
        }
      } catch (refreshErr) {
        console.warn('[Auth] Token refresh failed:', refreshErr);
      }
    }

    // All attempts exhausted — clear state
    setUser(null);
    setPhotoUrl(null);
    setDisplayName(null);
    if (wasAuthenticated && typeof window !== 'undefined' && window.location.pathname !== '/login') {
      console.warn('[Auth] Session expired, redirecting to /login');
      window.location.href = '/login';
    }
  }, []);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    setIsReady(false);
    try {
      await checkAuthStatus();
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }, [checkAuthStatus]);

  const updatePhotoUrl = useCallback((url: string | null) => {
    setPhotoUrl(url);
  }, []);

  // Start proactive token refresh interval (23h, well before the 24h accessToken cookie expiry)
  const startRefreshTimer = useCallback(() => {
    clearRefreshTimer();
    const REFRESH_INTERVAL = 23 * 60 * 60 * 1000; // 23 hours, well before 24h expiry
    refreshTimer.current = window.setInterval(async () => {
      try {
        const { authService } = await import('@/services/auth-service');
        const refreshed = await authService.refreshSession();
        if (refreshed) {
          await checkAuthStatus();
        } else {
          console.warn('[Auth] Proactive refresh returned false, logging out');
          performLogout({ redirect: true });
        }
      } catch (err) {
        console.warn('[Auth] Proactive refresh failed, logging out:', err);
        performLogout({ redirect: true });
      }
    }, REFRESH_INTERVAL) as unknown as number;
  }, [clearRefreshTimer, checkAuthStatus, performLogout]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let mounted = true;
    setIsReady(false);
    (async () => {
      try {
        // Check authentication status via cookies instead of localStorage
        await checkAuthStatus();
      } catch (e) {
        console.error('Failed to initialise auth context:', e);
        if (mounted) setUser(null);
      } finally {
        if (mounted) setIsLoading(false);
        if (mounted) setIsReady(true);
      }
    })();

    return () => {
      mounted = false;
      clearRefreshTimer();
    };
  }, [clearRefreshTimer, checkAuthStatus]);

  // When user becomes authenticated, start the proactive refresh timer
  useEffect(() => {
    if (user) {
      startRefreshTimer();
    } else {
      clearRefreshTimer();
    }
  }, [user, startRefreshTimer, clearRefreshTimer]);

  // Refresh tokens when tab becomes visible again (handles laptop sleep / tab suspension)
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible' || !userRef.current) return;
      try {
        const { authService } = await import('@/services/auth-service');
        const refreshed = await authService.refreshSession();
        if (refreshed) {
          await checkAuthStatus();
          startRefreshTimer(); // Reset the refresh interval
        }
      } catch {
        // checkAuthStatus handles logout if refresh truly failed
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAuthStatus, startRefreshTimer]);

  const value = useMemo<AuthContextType>(
      () => ({
        user,
        isAuthenticated: !!user,
        isLoading,
        isReady,
        photoUrl,
        displayName,
        updatePhotoUrl,
        login,
        logout,
        setNewPassword,
        refreshUser,
      }),
      [isLoading, isReady, login, logout, refreshUser, setNewPassword, user, photoUrl, displayName, updatePhotoUrl],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
